import React from 'react';
import { useMutation } from '@apollo/client';
import TrackerEditQuickMealMutation from './TrackerEditQuickMealMutation';
import TrackerQuery from '../Fetch/TrackerQuery';
import FetchMealQuery from '../FetchMeal/FetchMealQuery';
import FetchUserMealsQuery from '../../UserMeals/Fetch/FetchUserMealsQuery';
import { formatIsoDate } from '../../../Helpers/dateFormatter';
import type { EditQuickMealInput, QuickMealMutationResult } from '../TrackerAddQuickMeal/types';

export type EditQuickMealFn = (input: EditQuickMealInput) => Promise<QuickMealMutationResult>;

interface ITrackerEditQuickMealConnectorApolloProps {
  children: ({ editQuickMeal }: { editQuickMeal: EditQuickMealFn }) => React.ReactElement;
  post?: ((message: string) => void) | null;
  /** Date the meal was logged on before the edit; refetched too when the meal moves to another day. */
  originalDate: string;
}

/**
 * Mirrors `TrackerEditMealConnectorApollo`. The meal itself is always refetched so the details page
 * shows the saved values; a logged meal also refreshes the tracker day(s), a template the Your Meals
 * list (its card shows calories).
 */
const TrackerEditQuickMealConnectorApollo: React.FC<ITrackerEditQuickMealConnectorApolloProps> = ({
  children,
  post,
  originalDate,
}) => {
  const [editQuickMealMutation] = useMutation(TrackerEditQuickMealMutation);

  const editQuickMeal: EditQuickMealFn = async (input) => {
    const refetchQueries: { query: unknown; variables?: Record<string, unknown> }[] = [
      { query: FetchMealQuery, variables: { id: input.mealId } },
    ];

    if (input.dateTime) {
      const date = formatIsoDate(new Date(input.dateTime));
      refetchQueries.push({ query: TrackerQuery, variables: { date } });
      if (originalDate !== date) {
        refetchQueries.push({ query: TrackerQuery, variables: { date: originalDate } });
      }
    } else {
      refetchQueries.push({ query: FetchUserMealsQuery });
    }

    const response = await editQuickMealMutation({
      variables: { input },
      awaitRefetchQueries: true,
      refetchQueries: refetchQueries as never,
    });

    if (post) {
      post('reload');
    }

    return response.data.dailyTracker.editQuickMeal;
  };

  return children({ editQuickMeal });
};

export default TrackerEditQuickMealConnectorApollo;
